import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Card, Badge } from 'flowbite-react';
import { useDatabase } from '../../hooks/useDatabase';
import { useResource } from '../../hooks/useResource';
import { useWorkspace } from '../../context/WorkspaceContext';
import EditableTitle from '../../components/EditableTitle';
import ResourceReservationPanel from '../../components/planning/ResourceReservationPanel';

const ResourceDetailPage: React.FC<{ overrideResourceId?: string }> = ({ overrideResourceId }) => {
  const { resourceId: paramId } = useParams<{ resourceId: string }>();
  const { workspaceId } = useWorkspace();
  const resourceId = overrideResourceId || paramId;
  const { resource, loading, error } = useResource(resourceId);
  const { applyCommand, database } = useDatabase();

  // Default window: 6 weeks starting Monday of the current week.
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    const dow = now.getDay(); // 0=Sun .. 6=Sat
    const back = (dow + 6) % 7; // back to Monday
    const start = new Date(now);
    start.setDate(start.getDate() - back);
    const end = new Date(start);
    end.setDate(start.getDate() + 41); // 6 weeks
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    };
  }, []);

  const teams = useMemo(() => {
    if (!database) return [];
    return Object.entries(database.teams()).map(([id, t]: [string, any]) => ({ id, name: t.name() }));
  }, [database]);

  const updateName = async (newName: string) => {
    if (!resourceId) return;
    await applyCommand({
      type: 'UpdateResource',
      id: resourceId,
      actions: [{ type: 'SetName', name: newName }],
    });
  };

  const updateDescription = async (newDescription: string) => {
    if (!resourceId) return;
    await applyCommand({
      type: 'UpdateResource',
      id: resourceId,
      actions: [{ type: 'SetDescription', description: newDescription || null }],
    });
  };

  const updateTeam = async (teamId: string) => {
    if (!resourceId) return;
    await applyCommand({
      type: 'UpdateResource',
      id: resourceId,
      actions: [{ type: 'SetManagedByTeam', team_id: teamId || null }],
    });
  };

  const updateConcurrency = async (raw: string) => {
    if (!resourceId) return;
    const n = parseInt(raw, 10);
    const limit = Number.isFinite(n) && n > 0 ? n : null;
    await applyCommand({
      type: 'UpdateResource',
      id: resourceId,
      actions: [{ type: 'SetConcurrencyLimit', limit }],
    });
  };

  const updateCooldown = async (raw: string) => {
    if (!resourceId) return;
    const n = parseInt(raw, 10);
    const days = Number.isFinite(n) && n > 0 ? n : null;
    await applyCommand({
      type: 'UpdateResource',
      id: resourceId,
      actions: [{ type: 'SetCooldownDays', days }],
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="text-center py-12 text-cm-text-muted">
        {error || 'Resource not found'}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <EditableTitle title={resource.name} onTitleChange={updateName} />
        {resource.concurrencyLimit !== undefined && (
          <Badge color="info">concurrency {resource.concurrencyLimit}</Badge>
        )}
      </div>

      <Card>
        <h3 className="font-mono font-medium text-cm-text-primary mb-3">Details</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-cm-text-muted block mb-1">Description</label>
            <textarea
              className="w-full bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-3 text-cm-text-primary text-sm resize-y min-h-[80px]"
              value={resource.description || ''}
              onChange={(e) => updateDescription(e.target.value)}
              placeholder="What is this resource used for?"
            />
          </div>

          <div>
            <label className="text-sm text-cm-text-muted block mb-1">Managed by team</label>
            <select
              className="w-full bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-2 text-cm-text-primary text-sm"
              value={resource.managedByTeamId || ''}
              onChange={(e) => updateTeam(e.target.value)}
            >
              <option value="">— none —</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-cm-text-muted block mb-1">
                Concurrency limit <span className="text-xs">(0 = unlimited)</span>
              </label>
              <input
                type="number"
                min={0}
                className="w-full bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-2 text-cm-text-primary text-sm"
                value={resource.concurrencyLimit ?? 0}
                onChange={(e) => updateConcurrency(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm text-cm-text-muted block mb-1">
                Cooldown (days) <span className="text-xs">(0 = none)</span>
              </label>
              <input
                type="number"
                min={0}
                className="w-full bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-2 text-cm-text-primary text-sm"
                value={resource.cooldownDays ?? 0}
                onChange={(e) => updateCooldown(e.target.value)}
              />
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="font-mono font-medium text-cm-text-primary mb-3">Reservations</h3>
        {resourceId && (
          <ResourceReservationPanel
            resourceIds={[resourceId]}
            startDate={startDate}
            endDate={endDate}
            intervalDays={7}
            workspaceId={workspaceId}
            emptyMessage="This resource has no reservations in the current window."
          />
        )}
      </Card>
    </div>
  );
};

export default ResourceDetailPage;
